import React, { FC, ReactNode, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { Button, LinkButton, Loader } from 'src/components';
import { DashboardApi } from 'src/apiClassic';
import { AllCoursesResponse } from 'src/apiClassic/dashboard/schema';
import { CollapsibleCardClassic } from 'src/apps/Classic/CollapsibleCardClassic';
import { MigrationBannerModal } from 'src/apps/Classic/MigrationBannerModal';
import { intlGetMessagesLocale } from 'src/utils/intl';
import { communityCoursesBlogPosts } from './migration.const';
import * as S from './styles';

const msgs = defineMessages({
  headerUGC: { id: 'migration_ugc_phase2_header' },
  headerHybrid: { id: 'migration_hybrid_phase2_header' },
  headerIntro: { id: 'migration_phase2_header_intro' },
  descriptionUGC: { id: 'migration_ugc_description' },
  descriptionHybrid: { id: 'migration_hybrid_phase2_description1' },
  descriptionHybrid2: { id: 'migration_hybrid_phase2_description2' },
  learnMore: { id: 'migration_learn_more_cta' },
  tryOfficial: { id: 'classic_ugc_move_cta' },
  continue: { id: 'Continue' },
});

const MemriseCreatorID = 2224242;

export const CommunityCoursesBanner: FC = () => {
  const intl = useIntl();
  const [migrationBannerOpen, setMigrationBannerOpen] = useState(false);

  const { data: allCourses, isLoading } = useQuery<AllCoursesResponse | null>({
    queryKey: ['allCourses'],
    queryFn: () => {
      return DashboardApi.getAllCourses();
    },
  });

  const coursesUGC = allCourses?.courses.filter(course => course?.creatorId !== MemriseCreatorID);
  const lengthCoursesUGC = coursesUGC?.length;

  const coursesMemrise =
    allCourses?.courses.filter(course => course?.creatorId === MemriseCreatorID) || undefined;
  const lengthCoursesMemrise = coursesMemrise?.length;

  return (
    <S.ModalWrapper isOpen hideCloseButton fullSize>
      {isLoading ? (
        <S.LoaderContainer>
          <Loader />
        </S.LoaderContainer>
      ) : (
        <>
          <S.ModalContent>
            <S.Header>
              <S.Title>
                {intl.formatMessage(lengthCoursesMemrise ? msgs.headerHybrid : msgs.headerUGC)}
              </S.Title>
              <S.TitleIntro>
                {intl.formatMessage(msgs.headerIntro, {
                  bold: (...chunks: ReactNode[]) => (
                    <S.BoldText key="BoldText">{chunks}</S.BoldText>
                  ),
                })}
              </S.TitleIntro>
            </S.Header>
            <S.Content>
              {lengthCoursesMemrise ? (
                <>
                  <S.DescriptionHybrid>
                    {intl.formatMessage(msgs.descriptionHybrid)}
                  </S.DescriptionHybrid>
                  <S.DescriptionHybridSecondary>
                    {intl.formatMessage(msgs.descriptionHybrid2)}
                  </S.DescriptionHybridSecondary>
                </>
              ) : (
                <S.DescriptionUGC>{intl.formatMessage(msgs.descriptionUGC)}</S.DescriptionUGC>
              )}
              {!!lengthCoursesUGC && (
                <CollapsibleCardClassic
                  numberOfCourses={lengthCoursesUGC}
                  listOfCourses={coursesUGC}
                  isOfficial={false}
                  pureUGC={!lengthCoursesMemrise}
                />
              )}
              {!!lengthCoursesMemrise && (
                <CollapsibleCardClassic
                  numberOfCourses={lengthCoursesMemrise}
                  listOfCourses={coursesMemrise}
                  isOfficial
                />
              )}
            </S.Content>
          </S.ModalContent>
          <S.ButtonWrapperFooter>
            <LinkButton
              buttonType="primary"
              buttonSize="medium"
              href={
                communityCoursesBlogPosts[intlGetMessagesLocale(intl.locale)] ||
                communityCoursesBlogPosts.en
              }
              target="_blank"
            >
              {intl.formatMessage(msgs.learnMore)}
            </LinkButton>
            {!lengthCoursesMemrise && (
              <Button
                buttonType="secondary"
                buttonSize="medium"
                onClick={() => setMigrationBannerOpen(true)}
              >
                {intl.formatMessage(msgs.tryOfficial)}
              </Button>
            )}
          </S.ButtonWrapperFooter>
          <MigrationBannerModal
            isOpen={migrationBannerOpen}
            onClose={() => setMigrationBannerOpen(false)}
          />
        </>
      )}
    </S.ModalWrapper>
  );
};
